// The room's drawn textures (RoomModel.ts), made on canvases at start-up instead of downloaded: the damask
// wallpaper, the chairs' striped upholstery, the bench's buttoned (tufted) leather, the scoreboard, the club sign and
// a landscape painting and an old club photograph. All are deterministic (seeded) and the repeating ones tile. textures.ts caches each canvas
// and scales it for the graphics quality.
//
// Sizes: wallpaper 512x512, stripe 256x256, tufted 256x256, scoreboard 1024x512, sign 512x256, painting 512x256, photo 384x192.

type Ctx = CanvasRenderingContext2D;
// The canvases are drawn once, read back for the grain (getImageData) and uploaded as textures: a CPU canvas
// (willReadFrequently) avoids a slow GPU read-back per texture, most of the load time on software-GL machines.

function canvas(w: number, h: number): HTMLCanvasElement {
    const c = document.createElement("canvas");
    c.width = w; c.height = h;
    return c;
}

/** Small seeded PRNG (mulberry32), so the textures are the same on every load. */
function rng(seed: number) {
    let a = seed >>> 0;
    return () => {
        a = (a + 0x6D2B79F5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

/** Tileable value noise on a period x period lattice, smooth (cubic) between the lattice points. */
function valueNoise(period: number, seed: number) {
    const r = rng(seed);
    const grid = new Float32Array(period * period);
    for (let i = 0; i < grid.length; i++) grid[i] = r() * 2 - 1;
    const s = (t: number) => t * t * (3 - 2 * t);
    return (x: number, y: number) => {
        const x0 = Math.floor(x), y0 = Math.floor(y);
        const fx = s(x - x0), fy = s(y - y0);
        const ix = ((x0 % period) + period) % period, iy = ((y0 % period) + period) % period;
        const ix1 = (ix + 1) % period, iy1 = (iy + 1) % period;
        const a = grid[iy * period + ix], b = grid[iy * period + ix1], c = grid[iy1 * period + ix], d = grid[iy1 * period + ix1];
        return a + (b - a) * fx + (c - a) * fy + (a - b - c + d) * fx * fy;
    };
}

/** Per-pixel brightness variation over what is already drawn: f(x, y) returns a multiplier. */
function modulate(g: Ctx, w: number, h: number, f: (x: number, y: number) => number) {
    const img = g.getImageData(0, 0, w, h), d = img.data;
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            const k = f(x, y), i = (y * w + x) * 4;
            d[i] = Math.min(255, d[i] * k); d[i + 1] = Math.min(255, d[i + 1] * k); d[i + 2] = Math.min(255, d[i + 2] * k);
        }
    }
    g.putImageData(img, 0, 0);
}

// ---- wallpaper: claret damask, two motifs per tile (half drop), small rosettes between ----

function damaskMotif(g: Ctx, cx: number, cy: number, s: number) {
    g.save();
    g.translate(cx, cy);
    for (const side of [1, -1]) {
        g.save();
        g.scale(side, 1);
        // the outer scroll: from the top finial out and round to the foot
        g.beginPath();
        g.moveTo(0, -s);
        g.bezierCurveTo(0.22 * s, -0.78 * s, 0.62 * s, -0.62 * s, 0.52 * s, -0.18 * s);
        g.bezierCurveTo(0.44 * s, 0.18 * s, 0.16 * s, 0.42 * s, 0.03 * s, 0.96 * s);
        g.lineWidth = 0.055 * s;
        g.stroke();
        // its curl
        g.beginPath();
        g.arc(0.43 * s, -0.2 * s, 0.09 * s, -0.2, Math.PI * 1.3);
        g.stroke();
        // leaves
        for (const [x, y, rx, ry, rot] of [[0.27, -0.42, 0.17, 0.055, -0.75], [0.3, 0.12, 0.15, 0.05, 0.55], [0.17, 0.62, 0.12, 0.04, 0.95], [0.12, -0.78, 0.1, 0.035, -1.1]]) {
            g.beginPath();
            g.ellipse(x * s, y * s, rx * s, ry * s, rot, 0, 2 * Math.PI);
            g.fill();
        }
        g.restore();
    }
    // the central lozenge and flower
    g.beginPath();
    g.moveTo(0, -0.58 * s);
    g.quadraticCurveTo(0.2 * s, -0.05 * s, 0, 0.5 * s);
    g.quadraticCurveTo(-0.2 * s, -0.05 * s, 0, -0.58 * s);
    g.fill();
    for (let k = 0; k < 6; k++) {
        const a = k * Math.PI / 3;
        g.beginPath();
        g.ellipse(Math.cos(a) * 0.1 * s, -0.05 * s + Math.sin(a) * 0.1 * s, 0.07 * s, 0.035 * s, a, 0, 2 * Math.PI);
        g.fill();
    }
    g.beginPath(); g.arc(0, -1.04 * s, 0.05 * s, 0, 2 * Math.PI); g.fill();
    g.restore();
}

function rosette(g: Ctx, cx: number, cy: number, s: number) {
    for (let k = 0; k < 4; k++) {
        const a = k * Math.PI / 2;
        g.beginPath();
        g.ellipse(cx + Math.cos(a) * 0.12 * s, cy + Math.sin(a) * 0.12 * s, 0.11 * s, 0.045 * s, a, 0, 2 * Math.PI);
        g.fill();
    }
    g.beginPath(); g.arc(cx, cy, 0.05 * s, 0, 2 * Math.PI); g.fill();
}

export function drawWallpaper(): HTMLCanvasElement {
    const n = 512, c = canvas(n, n), g = c.getContext("2d", {willReadFrequently: true});
    if (!g) return c;
    g.fillStyle = "rgb(112,66,62)";
    g.fillRect(0, 0, n, n);
    // faint satin stripes behind the pattern
    for (let x = 0; x < n; x += 64) { g.fillStyle = "rgba(255,235,225,0.035)"; g.fillRect(x + 20, 0, 24, n); }
    const draw = (fill: string, dx: number, dy: number) => {
        g.fillStyle = fill; g.strokeStyle = fill;
        for (const [x, y] of [[128, 128], [384, 384]]) damaskMotif(g, x + dx, y + dy, 104);
        for (const [x, y] of [[384, 128], [128, 384]]) rosette(g, x + dx, y + dy, 104);
    };
    // a light edge up-left and the darker, slightly richer motif on top: printed flock, slightly raised
    draw("rgba(160,104,96,0.55)", -1, -1);
    draw("rgb(92,48,46)", 0, 0);
    const paper = valueNoise(128, 51), cloud = valueNoise(8, 52);
    modulate(g, n, n, (x, y) => 1 + 0.035 * paper(x / 4, y / 4) + 0.03 * cloud(x / 64, y / 64));
    return c;
}

// ---- chairs: regency-striped upholstery (dark green, wide and narrow stripes, a fine weave) ----

export function drawStripeFabric(): HTMLCanvasElement {
    const n = 256, c = canvas(n, n), g = c.getContext("2d", {willReadFrequently: true});
    if (!g) return c;
    g.fillStyle = "rgb(34,72,50)";
    g.fillRect(0, 0, n, n);
    // one repeat of the stripe across the tile: wide satin stripe, gold pinstripes, a darker band
    g.fillStyle = "rgb(46,92,64)"; g.fillRect(16, 0, 72, n);
    g.fillStyle = "rgb(176,148,82)"; g.fillRect(100, 0, 4, n); g.fillRect(108, 0, 4, n);
    g.fillStyle = "rgb(26,56,40)"; g.fillRect(128, 0, 56, n);
    g.fillStyle = "rgb(46,92,64)"; g.fillRect(200, 0, 24, n);
    g.fillStyle = "rgb(176,148,82)"; g.fillRect(236, 0, 3, n);
    const nap = valueNoise(32, 61);
    modulate(g, n, n, (x, y) => {
        const weave = ((x + (y >> 1)) & 3) < 2 ? 1.04 : 0.96;      // twill
        return weave * (1 + 0.06 * nap(x / 8, y / 8));
    });
    return c;
}

// ---- bench: buttoned (tufted) oxblood leather, a diamond pattern of buttons with creases between them ----

export function drawTufted(): HTMLCanvasElement {
    const n = 256, c = canvas(n, n), g = c.getContext("2d", {willReadFrequently: true});
    if (!g) return c;
    g.fillStyle = "rgb(118,34,30)";
    g.fillRect(0, 0, n, n);
    const pebble = valueNoise(64, 71), sheen = valueNoise(4, 72);
    const half = n / 2;
    modulate(g, n, n, (x, y) => {
        // nearest button: the lattice (0,0), (128,128) and their repeats
        const dx1 = Math.abs(((x + half) % n) - half), dy1 = Math.abs(((y + half) % n) - half);
        const dx2 = Math.abs((x % n) - half), dy2 = Math.abs((y % n) - half);
        const d = Math.min(Math.hypot(dx1, dy1), Math.hypot(dx2, dy2));
        // creases along the diamond's edges, the lines x + y and x - y = multiples of 256 through the buttons
        const l1 = (half - Math.abs((((x + y) % n) + n) % n - half)) / Math.SQRT2;
        const l2 = (half - Math.abs((((x - y) % n) + n) % n - half)) / Math.SQRT2;
        const crease = Math.exp(-(l1 * l1) / 6) + Math.exp(-(l2 * l2) / 6);
        const puff = Math.min(1, d / 70);                                  // the pillow between the buttons
        return (0.72 + 0.36 * puff - 0.16 * Math.exp(-(d * d) / 300) - 0.08 * crease * (1 - puff * 0.6))
            * (1 + 0.05 * pebble(x / 4, y / 4) + 0.05 * sheen(x / 64, y / 64));
    });
    // the buttons
    for (const [x, y] of [[0, 0], [n, 0], [0, n], [n, n], [half, half]]) {
        const grad = g.createRadialGradient(x - 1.5, y - 1.5, 0.5, x, y, 7);
        grad.addColorStop(0, "rgb(150,58,50)"); grad.addColorStop(0.7, "rgb(70,18,16)"); grad.addColorStop(1, "rgba(60,14,12,0)");
        g.fillStyle = grad;
        g.beginPath(); g.arc(x, y, 7, 0, 2 * Math.PI); g.fill();
    }
    return c;
}

// ---- the scoreboard, the club sign and a landscape painting ----

/** A traditional snooker scoreboard: two sliding-pointer scales to 100 and the frame counters. */
export function drawScoreboard(): HTMLCanvasElement {
    const c = canvas(1024, 512), g = c.getContext("2d", {willReadFrequently: true});
    if (!g) return c;
    const grad = g.createLinearGradient(0, 0, 0, 512);
    grad.addColorStop(0, "#3a1c10"); grad.addColorStop(1, "#2a130a");
    g.fillStyle = grad; g.fillRect(0, 0, 1024, 512);
    g.strokeStyle = "#c9a45a"; g.lineWidth = 6; g.strokeRect(14, 14, 996, 484);
    g.fillStyle = "#e3c27a"; g.textAlign = "center"; g.textBaseline = "middle";
    g.font = "bold 64px Georgia, 'Times New Roman', serif";
    g.fillText("SNOOKER", 512, 72);
    g.font = "26px Georgia, 'Times New Roman', serif";
    const rows = [{y: 190, label: "PLAYER 1", at: 37}, {y: 330, label: "PLAYER 2", at: 64}];
    for (const row of rows) {
        g.fillStyle = "#1a0c06"; g.fillRect(150, row.y - 26, 800, 52);
        g.strokeStyle = "#8a6a3a"; g.lineWidth = 2; g.strokeRect(150, row.y - 26, 800, 52);
        g.fillStyle = "#e3c27a"; g.textAlign = "left"; g.fillText(row.label.split(" ")[1], 100, row.y);
        g.textAlign = "center";
        for (let k = 0; k <= 100; k++) {
            const x = 170 + k * 7.6;
            g.fillStyle = "#c9a45a";
            g.fillRect(x - 1, row.y - 24, 2, k % 10 === 0 ? 20 : k % 5 === 0 ? 12 : 7);
            if (k % 10 === 0) { g.font = "18px Georgia, serif"; g.fillText(String(k), x, row.y + 12); }
        }
        const px = 170 + row.at * 7.6;
        g.fillStyle = "#f0d48a"; g.beginPath(); g.moveTo(px, row.y - 30); g.lineTo(px - 11, row.y - 48); g.lineTo(px + 11, row.y - 48); g.fill();
        g.font = "26px Georgia, 'Times New Roman', serif";
    }
    g.fillStyle = "#e3c27a"; g.font = "26px Georgia, serif"; g.fillText("FRAMES", 512, 420);
    for (const [x, n] of [[420, "2"], [604, "1"]] as [number, string][]) {
        g.fillStyle = "#1a0c06"; g.fillRect(x - 34, 440, 68, 50);
        g.strokeStyle = "#8a6a3a"; g.strokeRect(x - 34, 440, 68, 50);
        g.fillStyle = "#f2ead8"; g.font = "bold 36px Georgia, serif"; g.fillText(n, x, 466);
    }
    return c;
}

/** The club's sign: a rack of reds on green baize under the club name. */
export function drawSign(): HTMLCanvasElement {
    const c = canvas(512, 256), g = c.getContext("2d", {willReadFrequently: true});
    if (!g) return c;
    g.fillStyle = "#ece2c8"; g.fillRect(0, 0, 512, 256);
    g.fillStyle = "#1f5a33"; g.fillRect(20, 20, 472, 216);
    g.strokeStyle = "#c9a45a"; g.lineWidth = 3; g.strokeRect(30, 30, 452, 196);
    g.fillStyle = "#ece2c8"; g.textAlign = "center"; g.textBaseline = "middle";
    // the club name, shrunk if needed to fit inside the gold border (452 px) with a margin
    const title = "ONLINE SNOOKER CLUB";
    let size = 30;
    g.font = `bold ${size}px Georgia, 'Times New Roman', serif`;
    while (size > 16 && g.measureText(title).width > 412) { size--; g.font = `bold ${size}px Georgia, 'Times New Roman', serif`; }
    g.fillText(title, 256, 62);
    g.font = "16px Georgia, serif"; g.fillText("BILLIARDS  ·  SNOOKER  ·  EST. 1927", 256, 92);
    const r = 11;
    for (let row = 0; row < 5; row++) {
        for (let k = 0; k <= row; k++) {
            const x = 256 + (k - row / 2) * 2 * r, y = 128 + row * r * 1.75;
            g.fillStyle = "#b5161b"; g.beginPath(); g.arc(x, y, r - 0.5, 0, 2 * Math.PI); g.fill();
            g.fillStyle = "rgba(255,255,255,0.45)"; g.beginPath(); g.arc(x - 3, y - 3, 3, 0, 2 * Math.PI); g.fill();
        }
    }
    g.fillStyle = "#f4f1e8"; g.beginPath(); g.arc(256, 128 + 6 * r * 1.75 + 6, r - 0.5, 0, 2 * Math.PI); g.fill();
    return c;
}

/** An oil landscape: evening sky, hills fading into the distance, a lake and trees, under old varnish. */
export function drawPainting(): HTMLCanvasElement {
    const w = 512, h = 256, c = canvas(w, h), g = c.getContext("2d", {willReadFrequently: true});
    if (!g) return c;
    const r = rng(81);
    const sky = g.createLinearGradient(0, 0, 0, h * 0.62);
    sky.addColorStop(0, "#5f7f98"); sky.addColorStop(0.55, "#b9b59a"); sky.addColorStop(1, "#e8c98e");
    g.fillStyle = sky; g.fillRect(0, 0, w, h);
    // the low sun's glow and a few clouds
    const sun = g.createRadialGradient(340, 150, 4, 340, 150, 120);
    sun.addColorStop(0, "rgba(255,236,190,0.9)"); sun.addColorStop(1, "rgba(255,236,190,0)");
    g.fillStyle = sun; g.fillRect(0, 0, w, h);
    for (let k = 0; k < 14; k++) {
        g.fillStyle = `rgba(240,226,210,${0.12 + 0.12 * r()})`;
        g.beginPath(); g.ellipse(r() * w, 20 + r() * 80, 30 + r() * 50, 6 + r() * 8, 0, 0, 2 * Math.PI); g.fill();
    }
    // hills, far to near
    const hill = (base: number, amp: number, colour: string, seed: number) => {
        const n = valueNoise(16, seed);
        g.fillStyle = colour;
        g.beginPath(); g.moveTo(0, h);
        for (let x = 0; x <= w; x += 4) g.lineTo(x, base - amp * (0.6 + 0.5 * n(x / 40, 0.5)));
        g.lineTo(w, h); g.closePath(); g.fill();
    };
    hill(150, 40, "#8c9a9a", 82);
    hill(165, 30, "#6f8370", 83);
    hill(185, 24, "#4e6a44", 84);
    // the lake, with the sky in it
    const lake = g.createLinearGradient(0, 178, 0, 210);
    lake.addColorStop(0, "#d9c28f"); lake.addColorStop(1, "#7f8f8a");
    g.fillStyle = lake; g.beginPath(); g.ellipse(300, 196, 170, 16, 0, 0, 2 * Math.PI); g.fill();
    // the near meadow and trees
    g.fillStyle = "#3f5a2e"; g.beginPath(); g.moveTo(0, h); g.lineTo(0, 205);
    g.quadraticCurveTo(200, 196, 512, 214); g.lineTo(512, h); g.fill();
    const tree = (x: number, y: number, s: number) => {
        g.fillStyle = "#3a2a1a"; g.fillRect(x - s * 0.06, y - s * 0.5, s * 0.12, s * 0.5);
        for (let k = 0; k < 9; k++) {
            g.fillStyle = k % 3 ? "#2f4a26" : "#3d5c2f";
            g.beginPath(); g.arc(x + (r() - 0.5) * s * 0.7, y - s * 0.55 - r() * s * 0.6, s * (0.22 + r() * 0.12), 0, 2 * Math.PI); g.fill();
        }
    };
    tree(70, 222, 80); tree(120, 214, 56); tree(455, 226, 90); tree(410, 218, 50);
    // brush texture, varnish and a darker edge
    const brush = valueNoise(64, 85);
    modulate(g, w, h, (x, y) => {
        const vx = (x - w / 2) / (w / 2), vy = (y - h / 2) / (h / 2);
        return (1 + 0.05 * brush(x / 3, y / 6)) * (1 - 0.22 * (vx * vx + vy * vy) * 0.5);
    });
    g.fillStyle = "rgba(160,120,40,0.10)"; g.fillRect(0, 0, w, h);
    return c;
}

/** An old sepia photograph of the club: a table under its lamp, a rack of balls, a caption on the mount. */
export function drawPhoto(): HTMLCanvasElement {
    const w = 384, h = 192, c = canvas(w, h), g = c.getContext("2d", {willReadFrequently: true});
    if (!g) return c;
    // the cream mount and the print
    g.fillStyle = "#e6dcc4"; g.fillRect(0, 0, w, h);
    const px = 22, py = 16, pw = w - 44, ph = h - 52;
    const wall = g.createLinearGradient(0, py, 0, py + ph);
    wall.addColorStop(0, "#5a4632"); wall.addColorStop(0.6, "#7d6448"); wall.addColorStop(1, "#4a3826");
    g.fillStyle = wall; g.fillRect(px, py, pw, ph);
    // lamp light pooled on the table
    const glow = g.createRadialGradient(w / 2, py + ph * 0.62, 6, w / 2, py + ph * 0.62, pw * 0.45);
    glow.addColorStop(0, "rgba(236,214,170,0.75)"); glow.addColorStop(1, "rgba(236,214,170,0)");
    g.fillStyle = glow; g.fillRect(px, py, pw, ph);
    // the table in perspective, its legs and the lamp above
    const ty = py + ph * 0.55;
    g.fillStyle = "#3a2a1a";
    g.beginPath(); g.moveTo(w / 2 - 90, ty + 34); g.lineTo(w / 2 + 90, ty + 34); g.lineTo(w / 2 + 62, ty); g.lineTo(w / 2 - 62, ty); g.fill();
    g.fillStyle = "#9c8a68";
    g.beginPath(); g.moveTo(w / 2 - 82, ty + 30); g.lineTo(w / 2 + 82, ty + 30); g.lineTo(w / 2 + 58, ty + 4); g.lineTo(w / 2 - 58, ty + 4); g.fill();
    g.fillStyle = "#2e2216";
    for (const x of [w / 2 - 84, w / 2 - 20, w / 2 + 16, w / 2 + 80]) g.fillRect(x, ty + 34, 5, 26);
    g.fillRect(w / 2 - 70, py + 22, 140, 9);
    g.fillRect(w / 2 - 1, py, 2, 22);
    // a rack of balls and two figures with cues
    g.fillStyle = "#4a3a28";
    for (let row = 0; row < 4; row++) for (let k = 0; k <= row; k++) { g.beginPath(); g.arc(w / 2 + 20 + row * 4, ty + 16 + (k - row / 2) * 5, 2, 0, 2 * Math.PI); g.fill(); }
    const figure = (x: number, s: number) => {
        g.fillStyle = "#241a10";
        g.beginPath(); g.arc(x, py + ph * 0.3, 7 * s, 0, 2 * Math.PI); g.fill();
        g.fillRect(x - 9 * s, py + ph * 0.3 + 7 * s, 18 * s, ph * 0.62);
        g.strokeStyle = "#241a10"; g.lineWidth = 2; g.beginPath(); g.moveTo(x + 6 * s, py + ph * 0.42); g.lineTo(x + 10 * s, py + ph); g.stroke();
    };
    figure(px + 44, 1.1); figure(px + pw - 40, 1.0);
    // grain, fading and a darker edge, then the caption
    const grain = valueNoise(48, 91);
    modulate(g, w, h, (x, y) => {
        if (x < px || x >= px + pw || y < py || y >= py + ph) return 1;
        const vx = (x - w / 2) / (pw / 2), vy = (y - (py + ph / 2)) / (ph / 2);
        return (1 + 0.06 * grain(x / 2, y / 2)) * (1 - 0.3 * (vx * vx + vy * vy) * 0.5);
    });
    g.fillStyle = "#4a3a28"; g.textAlign = "center"; g.textBaseline = "middle";
    g.font = "italic 15px Georgia, 'Times New Roman', serif";
    g.fillText("Club championship, 1927", w / 2, h - 18);
    return c;
}
